import React, { useEffect, useMemo, useState } from "react";

/* ----------------------------------------------------------------------- */
/*  Static config                                                          */
/* ----------------------------------------------------------------------- */

const BOARDS = [
  { key: "forest", label: "Forest felt", swatch: "#2E4A3B", base: "#2E4A3B", glow: "#3C5C4A" },
  { key: "charcoal", label: "Charcoal", swatch: "#2A2521", base: "#26221E", glow: "#332C26" },
  { key: "oxblood", label: "Oxblood", swatch: "#5C2C27", base: "#4E2420", glow: "#5E2E28" },
  { key: "slate", label: "Warm slate", swatch: "#45565F", base: "#3C4A52", glow: "#4A5C66" },
];

const TAG_META = {
  puzzles: { label: "Puzzles", color: "#1D3557" },
  set: { label: "Set", color: "#C0392B" },
  story: { label: "Story", color: "#B07A1B" },
  gameplay: { label: "Gameplay", color: "#2F6E5A" },
};
const TAG_ORDER = ["puzzles", "set", "story", "gameplay"];

const PAGE_SIZE = 30;

function rankColor(r) {
  return r === 1 ? "#D99A1C" : r === 2 ? "#8895A6" : r === 3 ? "#B5743A" : "#1D3557";
}

function statusMeta(s) {
  if (s === "open") return { label: "OPEN", color: "#2F6E5A" };
  if (s === "closed") return { label: "CLOSED", color: "#9a8f78" };
  if (s === "moved") return { label: "MOVED", color: "#C8962A" };
  if (s === "limited") return { label: "LIMITED", color: "#C8962A" };
  return { label: "PLAYED", color: "#9a8f78" };
}

function shortUrl(u) {
  return (u || "").replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
}

function readBoard() {
  try {
    return localStorage.getItem("theyescape-board") || "forest";
  } catch (e) {
    return "forest";
  }
}

/* ----------------------------------------------------------------------- */
/*  Collapsible memo (About / Rubric)                                      */
/* ----------------------------------------------------------------------- */

function Memo({ pin, eyebrow, title, open, onToggle, children }) {
  return (
    <div className="te-memo">
      <span className="te-pin" style={{ background: pin }} />
      <div className="te-memo-head" onClick={onToggle}>
        <div className="te-memo-titles">
          <span className="te-eyebrow te-eyebrow--memo">{eyebrow}</span>
          <span className="te-memo-title">{title}</span>
        </div>
        <span className="te-memo-caret">{open ? "–" : "+"}</span>
      </div>
      {open && <div className="te-memo-body">{children}</div>}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/*  Rubric + tags reference content                                        */
/* ----------------------------------------------------------------------- */

const RUBRIC = [
  { n: 5, color: "#D99A1C", title: "Excellent", text: "memorable and excellent; put it on your itinerary, likely the highlight of a trip." },
  { n: 4, color: "#2F6E5A", title: "Good", text: "a little something that makes it distinctive; we happily recommend it." },
  { n: 3, color: "#1D3557", title: "Decent", text: "solid but didn’t fully distinguish itself; the higher end is still worth a visit." },
  { n: 2, color: "#C0392B", title: "Flawed", text: "something frustrating makes it worse than a standard room; not recommended." },
  { n: 1, color: "#8a7e68", title: "Bad", text: "a waste of time; something ruined the experience." },
];

const TAG_GUIDE = [
  { key: "puzzles", text: "multiple puzzles better than standard, via interesting complexity or novel tech." },
  { key: "set", text: "the space feels realistic and richer than daily life: custom props, effects, varied rooms." },
  { key: "story", text: "a memorable plot or real lore; your actions have purpose, character, or a unique concept." },
  { key: "gameplay", text: "novel elements that significantly deviate from a standard escape room." },
];

function RubricContent() {
  return (
    <div className="te-rubric-grid">
      <div>
        <div className="te-mini-label">Rating rubric</div>
        {RUBRIC.map((r) => (
          <div className="te-rubric-row" key={r.n}>
            <span className="te-rubric-num" style={{ background: r.color }}>{r.n}</span>
            <div className="te-rubric-text">
              <strong>{r.title}</strong> — {r.text}
            </div>
          </div>
        ))}
      </div>
      <div>
        <div className="te-mini-label">Tags</div>
        {TAG_GUIDE.map((g) => (
          <div className="te-tagguide-row" key={g.key}>
            <span className="te-tag" style={{ background: TAG_META[g.key].color }}>
              {TAG_META[g.key].label}
            </span>
            <span className="te-tagguide-text"> — {g.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/*  A single case file card                                                */
/* ----------------------------------------------------------------------- */

function CaseCard({ room, open, onToggle }) {
  const sm = statusMeta(room.status);
  const rc = rankColor(room.rank);
  return (
    <div className={"te-card" + (open ? " is-open" : "")} onClick={onToggle}>
      <span className="te-pin" />
      <div className="te-card-top">
        <span className="te-caseno">CASE № {String(room.rank).padStart(3, "0")}</span>
        <span className="te-stamp" style={{ color: sm.color, borderColor: sm.color }}>
          {sm.label}
        </span>
      </div>

      <div className="te-card-head">
        <span className="te-medallion" style={{ background: rc }}>
          <i className="te-knob te-knob-r" style={{ background: rc }} />
          <i className="te-knob te-knob-b" style={{ background: rc }} />
          <b>{room.rank}</b>
        </span>
        <div className="te-titles">
          <div className="te-name">{room.name}</div>
          <div className="te-sub">{room.company} · {room.region}</div>
        </div>
      </div>

      <div className="te-tagrow">
        {room.tags.map((t) => {
          const m = TAG_META[t] || { label: t, color: "#7a6f5d" };
          return (
            <span className="te-tag" style={{ background: m.color }} key={t}>
              {m.label}
            </span>
          );
        })}
        <span className="te-rating">★ {room.rating}</span>
      </div>

      {open ? (
        <div className="te-debrief">
          {room.review.map((b, i) => (
            <div className={"te-line te-" + b.k} key={i}>
              {(b.k === "plus" || b.k === "minus") && (
                <span className="te-marker">{b.k === "plus" ? "+" : "–"}</span>
              )}
              <span className="te-linetext">{b.t}</span>
            </div>
          ))}
          <div className="te-card-meta">
            <span>⌖ {room.city}</span>
            <span>◷ {room.players} players</span>
            <a
              href={room.website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              ↗ {shortUrl(room.website)}
            </a>
          </div>
        </div>
      ) : (
        <div className="te-pull">▸ pull file to open debrief</div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/*  The board                                                              */
/* ----------------------------------------------------------------------- */

export default function Board({ rooms, regions, loading }) {
  const [search, setSearch] = useState("");
  const [region, setRegion] = useState("All");
  const [tags, setTags] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [board, setBoard] = useState(readBoard);
  const [aboutOpen, setAboutOpen] = useState(true);
  const [rubricOpen, setRubricOpen] = useState(false);
  const [limit, setLimit] = useState(PAGE_SIZE);

  // Keep the board background on the body too, so over-scroll matches.
  const bd = useMemo(
    () => BOARDS.find((b) => b.key === board) || BOARDS[0],
    [board]
  );
  useEffect(() => {
    document.body.style.background = bd.base;
    try {
      localStorage.setItem("theyescape-board", board);
    } catch (e) {}
  }, [bd, board]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rooms.filter((r) => {
      if (region !== "All" && r.region !== region) return false;
      if (tags.length && !tags.some((t) => r.tags.includes(t))) return false;
      if (s) {
        const hay = (
          r.name + " " + r.company + " " + r.region + " " + r.city + " " + r.tags.join(" ")
        ).toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [rooms, search, region, tags]);

  const visible = filtered.slice(0, limit);

  const regionCounts = useMemo(() => {
    const c = {};
    rooms.forEach((r) => {
      c[r.region] = (c[r.region] || 0) + 1;
    });
    return c;
  }, [rooms]);

  const resetLimit = () => setLimit(PAGE_SIZE);

  const toggleTag = (k) => {
    resetLimit();
    setTags((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : prev.concat(k)));
  };

  const toggleCard = (rank) => {
    setExpanded((prev) => Object.assign({}, prev, { [rank]: !prev[rank] }));
  };

  const pickBoard = (key) => setBoard(key);

  return (
    <div
      className="te-page"
      style={{ "--board-base": bd.base, "--board-glow": bd.glow }}
    >
      <div className="te-wrap">
        {/* ---------------- header ---------------- */}
        <header className="te-header">
          <div className="te-header-main">
            <div className="te-eyebrow">Escape Room Rankings</div>
            <h1 className="te-title">They Escape</h1>
            <p className="te-lede">
              Every room is a case on file — pull one to read the debrief.
            </p>
            <div className="te-count">
              {(rooms.length || 0)} ROOMS RANKED · {(regions.length || 0)} REGIONS
            </div>
          </div>
          <div className="te-boardpick">
            <span className="te-eyebrow te-eyebrow--board">Board color</span>
            <div className="te-board-chips">
              {BOARDS.map((b) => (
                <span
                  key={b.key}
                  className={"te-board-chip" + (board === b.key ? " is-active" : "")}
                  onClick={() => pickBoard(b.key)}
                >
                  <span className="te-board-swatch" style={{ background: b.swatch }} />
                  {b.label}
                </span>
              ))}
            </div>
          </div>
        </header>

        {/* ---------------- about ---------------- */}
        <Memo
          pin="#C0392B"
          eyebrow="Briefing"
          title="About these rankings"
          open={aboutOpen}
          onToggle={() => setAboutOpen((v) => !v)}
        >
          <p className="te-prose">
            These rankings are a guide by enthusiasts to help you get a sense of what to
            expect in a given escape room. They’ve been meticulously discussed and curated
            after playing 100+ quality rooms — we hope the direct comparison helps you gauge
            how good a room is relative to other reputable rooms. We favor unique, novel
            experiences over standard rooms with good polish, since standard rooms get less
            interesting once you’ve done a hundred of them. Because we mostly play rooms with
            strong reputations, the rankings are competitive:{" "}
            <span className="te-prose-accent">a low rank doesn’t mean a room is bad</span>.
            A room’s rating and review are usually more informative than its rank.
          </p>
        </Memo>

        {/* ---------------- rubric ---------------- */}
        <Memo
          pin="#1D3557"
          eyebrow="Reference"
          title="The rubric & tags"
          open={rubricOpen}
          onToggle={() => setRubricOpen((v) => !v)}
        >
          <RubricContent />
        </Memo>

        {/* ---------------- toolbar ---------------- */}
        <div className="te-toolbar">
          <div className="te-search">
            <span className="te-search-icon">⌕</span>
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                resetLimit();
              }}
              placeholder="Search rooms, companies, regions, tags…"
            />
          </div>
          <div className="te-toolbar-row">
            <div className="te-select">
              <select
                value={region}
                onChange={(e) => {
                  setRegion(e.target.value);
                  resetLimit();
                }}
              >
                <option value="All">All regions ({rooms.length})</option>
                {regions.map((rg) => (
                  <option value={rg} key={rg}>
                    {rg} ({regionCounts[rg] || 0})
                  </option>
                ))}
              </select>
              <span className="te-select-caret">▼</span>
            </div>
            <div className="te-evidence">
              <span className="te-mini-label te-mini-label--inline">Evidence</span>
              {TAG_ORDER.map((k) => {
                const active = tags.includes(k);
                const m = TAG_META[k];
                return (
                  <span
                    key={k}
                    className="te-chip"
                    onClick={() => toggleTag(k)}
                    style={{
                      borderColor: m.color,
                      color: active ? "#FBF5E6" : m.color,
                      background: active ? m.color : "transparent",
                    }}
                  >
                    {m.label}
                  </span>
                );
              })}
            </div>
            <div className="te-showing">
              Showing {visible.length} of {filtered.length}
            </div>
          </div>
        </div>

        {/* ---------------- results ---------------- */}
        {loading ? (
          <div className="te-loading">OPENING THE CASE FILES…</div>
        ) : filtered.length === 0 ? (
          <div className="te-empty">
            <div className="te-empty-title">No files match</div>
            <div className="te-empty-sub">Try clearing a filter or searching something else.</div>
          </div>
        ) : (
          <>
            <div className="te-grid">
              {visible.map((room) => (
                <CaseCard
                  key={room.rank}
                  room={room}
                  open={!!expanded[room.rank]}
                  onToggle={() => toggleCard(room.rank)}
                />
              ))}
            </div>
            {filtered.length > limit && (
              <div className="te-more-wrap">
                <span className="te-more" onClick={() => setLimit((l) => l + PAGE_SIZE)}>
                  Open {Math.min(PAGE_SIZE, filtered.length - limit)} more files ↓
                </span>
              </div>
            )}
          </>
        )}

        {/* ---------------- footer ---------------- */}
        <footer className="te-footer">
          <div className="te-footer-text">
            Previously <span className="te-footer-accent">theroomrank.com</span>.
          </div>
          <a
            className="te-kofi"
            href="https://ko-fi.com/M4M12G3W4"
            target="_blank"
            rel="noopener noreferrer"
          >
            ☕ Buy me a coffee
          </a>
        </footer>
      </div>
    </div>
  );
}
